import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Minus, Plus, Search, Share2, Trash2, Users, X } from "lucide-react";
import { confirmDialog } from "@/shared/components/dialog";
import { useAccountManager } from "@/features/account_manager/hooks/useAccountManager";
import { useAuth } from "@/features/login/hooks/useAuth";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";
import type WorkflowAccess from "../models/WorkflowAccess";
import type AutomationSummary from "../models/AutomationSummary";
import type { UserProfile } from "@/features/account_manager/models/UserProfile";

export interface ShareAutomationModalProps {
  automation: AutomationSummary;
  onClose: () => void;
}

/**
 * Comparte una automatización con los usuarios hijos directos del usuario actual.
 * El backend solo acepta hijos directos: el acceso baja un nivel a la vez, así que cada quien
 * reparte lo suyo y no se puede saltar al nieto.
 *
 * Una concesión da **ver y ejecutar**; publicar, eliminar y manejar el webhook siguen siendo del
 * dueño. Suspender una concesión corta también a todos los que colgaban de ella hacia abajo.
 */
export default function ShareAutomationModal({ automation, onClose }: ShareAutomationModalProps) {
  const { user: me } = useAuth();
  const { loading: accountsLoading, getPage } = useAccountManager();
  const { loading: accessLoading, getByWorkflow, grant, update, revoke } = useWorkflowAccess();

  const [accessList, setAccessList] = useState<WorkflowAccess[]>([]);
  const [childUsers, setChildUsers] = useState<UserProfile[]>([]);
  const [query, setQuery] = useState("");
  const [pendingUsers, setPendingUsers] = useState<Set<string>>(new Set());

  const loading = accountsLoading || accessLoading;

  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const grants = await getByWorkflow(automation.workflowId);
      if (!ignore && grants) setAccessList(grants);
    };
    fetch();
    return () => {
      ignore = true;
    };
  }, [automation.workflowId, getByWorkflow]);

  useEffect(() => {
    if (!me) return;
    let ignore = false;
    const fetchAccounts = async () => {
      const page = await getPage({ pageNumber: 1, pageSize: 1000 });
      if (!ignore && page) {
        setChildUsers(page.items.filter((u) => u.parentId === me.id));
      }
    };
    fetchAccounts();
    return () => {
      ignore = true;
    };
  }, [me, getPage]);

  /** userId → concesión, para resolver el estado de cada fila en O(1). */
  const accessByUser = useMemo(() => {
    const map = new Map<string, WorkflowAccess>();
    accessList.forEach((a) => map.set(a.grantedToUserId, a));
    return map;
  }, [accessList]);

  const visibleUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return childUsers;
    return childUsers.filter((u) => (u.name || u.email).toLowerCase().includes(q));
  }, [childUsers, query]);

  const withPending = useCallback(async (userId: string, action: () => Promise<void>) => {
    setPendingUsers((prev) => new Set(prev).add(userId));
    try {
      await action();
    } catch {
      // el toast de error lo dispara useAsync
    } finally {
      setPendingUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  }, []);

  /** Sin concesión → otorgar; con concesión → alternar entre activa y suspendida. */
  const handleToggle = (user: UserProfile) => {
    if (pendingUsers.has(user.id)) return;
    const existing = accessByUser.get(user.id);

    if (!existing) {
      void withPending(user.id, async () => {
        const created = await grant(automation.workflowId, user.id);
        if (created) setAccessList((prev) => [...prev, created]);
      });
      return;
    }

    void withPending(user.id, async () => {
      await update(automation.workflowId, existing.id, !existing.enabled);
      setAccessList((prev) =>
        prev.map((a) => (a.id === existing.id ? { ...a, enabled: !a.enabled } : a)),
      );
    });
  };

  const handleRevoke = async (access: WorkflowAccess, userName: string) => {
    const confirmed = await confirmDialog({
      title: "Revocar acceso",
      message: `¿Revocar el acceso de ${userName} a "${automation.name}"? Perderán el acceso también los usuarios a quienes se la haya compartido por debajo.`,
      confirmLabel: "Sí, revocar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;

    void withPending(access.grantedToUserId, async () => {
      await revoke(automation.workflowId, access.id);
      setAccessList((prev) => prev.filter((a) => a.id !== access.id));
    });
  };

  const activeCount = accessList.filter((a) => a.enabled).length;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative z-10 w-full max-w-lg overflow-hidden rounded-3xl border border-zinc-200/50 bg-white/85 p-6 shadow-2xl backdrop-blur-2xl"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 rounded-2xl bg-accent/10 p-3">
              <Share2 className="size-6 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-bold text-foreground" title={automation.name}>
                Compartir "{automation.name}"
              </h3>
              <p className="mt-0.5 text-sm text-foreground/50 leading-relaxed">
                Tus usuarios podrán verla y ejecutarla. Editarla, eliminarla y manejar su webhook
                siguen siendo solo tuyos.
              </p>
            </div>
            <button
              onClick={onClose}
              title="Cerrar"
              className="size-8 shrink-0 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/40 hover:bg-white hover:text-foreground transition-all"
            >
              <X className="size-4" />
            </button>
          </div>

          {childUsers.length > 5 && (
            <div className="relative mt-5">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground/30" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar usuario..."
                className="w-full rounded-xl border border-black/5 bg-white/70 py-2 pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-foreground/30 focus:border-accent/40"
              />
            </div>
          )}

          <div className="mt-4 max-h-72 space-y-1.5 overflow-y-auto pr-1">
            {loading && childUsers.length === 0 ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-2xl bg-black/5" />
              ))
            ) : childUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Users className="mb-3 size-10 text-foreground/20" />
                <p className="text-sm font-semibold text-foreground">Sin usuarios hijos</p>
                <p className="mt-1 text-xs text-foreground/50">
                  Creá una cuenta en Cuentas para poder compartirle esta automatización.
                </p>
              </div>
            ) : visibleUsers.length === 0 ? (
              <p className="py-8 text-center text-sm text-foreground/40">
                Sin usuarios para "{query.trim()}".
              </p>
            ) : (
              visibleUsers.map((user) => {
                const userName = user.name || user.email;
                const access = accessByUser.get(user.id);
                const pending = pendingUsers.has(user.id);
                return (
                  <div
                    key={user.id}
                    className="group flex items-center gap-3 rounded-2xl border border-black/5 bg-white/50 px-3 py-2.5 transition-colors hover:bg-white/80"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground" title={userName}>
                        {userName}
                      </p>
                      {user.name && (
                        <p className="truncate text-[11px] text-foreground/40">{user.email}</p>
                      )}
                    </div>

                    {access && !pending && (
                      <button
                        onClick={() => handleRevoke(access, userName)}
                        title="Revocar acceso"
                        className="size-8 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/40 opacity-0 transition-all hover:border-rose-500 hover:bg-rose-500 hover:text-white group-hover:opacity-100 focus-visible:opacity-100"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    )}

                    <button
                      onClick={() => handleToggle(user)}
                      disabled={pending}
                      title={
                        !access
                          ? `Compartir con ${userName}`
                          : access.enabled
                            ? "Con acceso — clic para suspender"
                            : "Suspendido — clic para reactivar"
                      }
                      className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all disabled:cursor-wait ${
                        !access
                          ? "border-dashed border-black/10 text-foreground/40 hover:border-accent/40 hover:bg-accent/5 hover:text-accent"
                          : access.enabled
                            ? "border-emerald-500/20 bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25"
                            : "border-gray-500/15 bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                      }`}
                    >
                      {pending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : !access ? (
                        <Plus className="size-3.5" />
                      ) : access.enabled ? (
                        <Check className="size-3.5" />
                      ) : (
                        <Minus className="size-3.5" />
                      )}
                      {!access ? "COMPARTIR" : access.enabled ? "CON ACCESO" : "SUSPENDIDO"}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {childUsers.length > 0 && (
            <p className="mt-4 border-t border-black/5 pt-3 text-[11px] font-medium text-foreground/40">
              {activeCount} {activeCount === 1 ? "usuario con acceso" : "usuarios con acceso"} ·{" "}
              suspender o revocar corta también a quienes lo recibieron por debajo.
            </p>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
